import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiEvent, ClickCopyBiEvent, BiData } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { useCopyToClipboard } from '../../../../utils/hooks/useCopyToClipboard';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { addNotification } from '../../../../Notifications/redux/actionCreators';
import { NotificationType } from '../../../../Notifications';

interface Props {
  deviation: PapiDeviation;
}

export function useCopyLinkAction(deviation: PapiDeviation): [() => void, BiEvent] {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard(deviation.shortUrl ?? '');
  const dispatchAddNotification = useAction(addNotification);

  const handleClick = useCallback(() => {
    if (deviation.shortUrl) {
      copyToClipboard();
    } else {
      dispatchAddNotification(NotificationType.Generic, {
        message: t('actionbar.stash.copyLink.notification.failure.text'),
      });
    }
  }, [t, deviation, dispatchAddNotification, copyToClipboard]);

  return [
    handleClick,
    BiData<ClickCopyBiEvent>({
      evid: 357,
      itemid: deviation.deviationId,
      typeid: getDeviationResourceType(deviation),
    }),
  ];
}

const CopyLinkButton: React.FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();

  const [handleClick, biData] = useCopyLinkAction(deviation);

  return (
    <GhostButton size="medium" variant="main" onClick={handleClick} biData={biData}>
      {t('actionbar.stash.copyLink.button.label')}
    </GhostButton>
  );
};

CopyLinkButton.displayName = 'CopyLinkButton';

export default CopyLinkButton;
